import type { Project } from '../types/platform';

export const PROJECT_STATUS_COLORS: Record<Project['status'], string> = {
  planning: '#94a3b8',
  ingesting: '#06b6d4',
  analyzing: '#f59e0b',
  transforming: '#0ea5e9',
  validating: '#8b5cf6',
  ams: '#10b981',
  failed: '#ef4444',
};

/** Operator-facing lifecycle labels (not backend implementation names). */
export const PROJECT_STATUS_LABELS: Record<Project['status'], string> = {
  planning: 'Uploading',
  ingesting: 'Ingesting',
  analyzing: 'Understanding',
  transforming: 'Parsing',
  validating: 'Validating',
  ams: 'Ready',
  failed: 'Failed',
};

const PROJECT_STATUSES = new Set<string>(Object.keys(PROJECT_STATUS_COLORS));

export function normalizeProjectStatus(status: unknown): Project['status'] {
  const value = typeof status === 'string' ? status.trim().toLowerCase() : '';
  if (value === 'complete' || value === 'ready' || value === 'operating' || value === 'completed') {
    return 'ams';
  }
  if (value === 'attention_required' || value === 'attention required') {
    return 'failed';
  }
  if (PROJECT_STATUSES.has(value)) {
    return value as Project['status'];
  }
  return 'planning';
}

export function getProjectStatusColor(status: unknown): string {
  const normalized = normalizeProjectStatus(status);
  return PROJECT_STATUS_COLORS[normalized];
}

export function getProjectStatusLabel(status: unknown): string {
  const normalized = normalizeProjectStatus(status);
  return PROJECT_STATUS_LABELS[normalized];
}

/**
 * Prefer attention when the project is Ready but has open high/critical incidents.
 * Does not invent status — overlays operational attention on Ready projects only.
 */
export function getOperationalProjectLabel(
  status: unknown,
  opts?: { needsAttention?: boolean },
): string {
  const normalized = normalizeProjectStatus(status);
  if (opts?.needsAttention && normalized === 'ams') {
    return 'Attention Required';
  }
  return PROJECT_STATUS_LABELS[normalized];
}

/** Avoid MUI alpha() on unknown values — uses rgba directly. */
export function hexToRgba(hex: string, opacity: number): string {
  const fallback = PROJECT_STATUS_COLORS.planning;
  const normalized = (hex || fallback).replace('#', '').trim();
  if (normalized.length !== 6) {
    return hexToRgba(fallback, opacity);
  }
  const r = parseInt(normalized.slice(0, 2), 16);
  const g = parseInt(normalized.slice(2, 4), 16);
  const b = parseInt(normalized.slice(4, 6), 16);
  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) {
    return hexToRgba(fallback, opacity);
  }
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

export function sanitizeProject(project: Partial<Project> & { id: string }): Project {
  return {
    id: project.id,
    name: project.name ?? 'Untitled project',
    sourceLanguage: project.sourceLanguage ?? '',
    targetLanguage: project.targetLanguage ?? '',
    status: normalizeProjectStatus(project.status),
    progress: typeof project.progress === 'number' ? project.progress : 0,
    createdAt: project.createdAt ?? new Date().toISOString(),
    updatedAt: project.updatedAt ?? new Date().toISOString(),
    worker: project.worker ?? '',
    ingestionStatus: project.ingestionStatus,
    ingestionError: project.ingestionError,
    ingestionPhase: project.ingestionPhase,
  };
}
